import React from 'react'
import { Outlet } from 'react-router-dom'

import { AvailableModules, Features } from 'generated/featureDefinitions'
import { PurchaseOrdersPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import type { ResponsivePagePrimaryAction } from 'modules/layout/components/ResponsivePage/_PageHeader/types'
import WithPaywall from 'modules/procurement/components/WithPaywall'
import PurchaseOrdersDataTable from 'modules/procurement/components/purchaseOrders/PurchaseOrdersDataTable'

import { PurchaseOrdersTab } from './types'

const PurchaseOrdersPage = () => {
  const [tab, setTab] = React.useState<PurchaseOrdersTab>('all')
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const { loading, data, error } = useQuery(PurchaseOrdersPageDocument, {
    variables: { companyId: company.id },
  })

  useEffect(() => {
    track(trackingEvents.procurement.openPurchaseOrders)
  }, [])

  const onTabChange = (newTab: PurchaseOrdersTab) => {
    setTab(newTab)
  }

  const canReadPurchaseOrder = data?.procurement?._policies?.purchaseOrderRead.can ?? false
  const canManageProcurementSettings = data?.procurement?._policies?.settingsManage.can ?? false
  const isPoTemplatesEnabled = !!data?.features?.poTemplatesFeature?.nodes?.some(
    (f) => f.name === 'dev_procurement_po_templates' && f.enabled
  )

  const actions: ResponsivePagePrimaryAction[] = canManageProcurementSettings
    ? [
        {
          icon: 'settings',
          to: navigation.settings.procurement.fullPath(),
          label: '',
          variant: 'secondary',
        },
      ]
    : []

  return (
    <WithPaywall
      allowWhileLoading
      features={[Features.procurement_purchase_orders]}
      modules={[AvailableModules.procurement]}
    >
      {() => (
        <ResponsivePage.Body
          variant="full"
          actions={{ primary: actions }}
          authorized={canReadPurchaseOrder}
          error={error}
          loading={loading}
        >
          <PurchaseOrdersDataTable
            currentTab={tab}
            onTabChange={onTabChange}
            canExport={isPoTemplatesEnabled}
          />
          <Outlet />
        </ResponsivePage.Body>
      )}
    </WithPaywall>
  )
}

export default PurchaseOrdersPage
